<template>
  <div
    class="fast-top-bar tw-flex tw-h-10 tw-items-center tw-justify-center tw-text-[16px] tw-leading-6"
  >
    <div
      class="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-between sm:tw-container"
    >
      <div class="tw-flex tw-h-full tw-items-center tw-gap-3">
        <div class="tw-flex tw-h-full tw-items-center tw-gap-1">
          <template v-if="contact.phoneNumbers.length > 0">
            <QIcon
              name="phone"
              size="18px"
              class="fast-top-bar-phone-icon tw-mr-2"
            />
            <span v-for="(tel, index) in contact.phoneNumbers" :key="tel">
              <ExternalLink
                :href="`tel:${tel}`"
                :title="$t('global.callNow')"
                @click="eventTopBarTel(tel)"
              >
                {{ $phoneNumberFormat(tel) }}
              </ExternalLink>
              <span v-if="contact.phoneNumbers.length - 1 > index">, </span>
            </span>
          </template>
        </div>

        <div
          v-if="hasAnySocial && showLocale"
          class="tw-flex tw-h-full tw-items-center tw-gap-2"
        >
          <span
            v-if="contact.phoneNumbers.length > 0"
            class="fast-top-bar-separator"
            aria-hidden="true"
          >
            |
          </span>
          <a
            v-for="(social, key) in socialLinks"
            v-show="social.link"
            :key="key"
            :href="social.link"
            :title="social.title"
            target="_blank"
            rel="noopener noreferrer nofollow"
            class="fast-top-bar-action"
            @click="eventTopBarsocial(key, social.link)"
          >
            <QIcon
              :name="social.icon"
              :size="social.size || '18px'"
              :style="social.style"
            />
          </a>
        </div>
      </div>

      <div class="tw-flex tw-h-full tw-items-center">
        <div v-if="hasAnySocial && !showLocale" class="tw-flex tw-h-full tw-items-center tw-gap-2">
          <a
            v-for="(social, key) in socialLinks"
            v-show="social.link"
            :key="key"
            :href="social.link"
            :title="social.title"
            target="_blank"
            rel="noopener noreferrer nofollow"
            class="fast-top-bar-action"
            @click="eventTopBarsocial(key, social.link)"
          >
            <QIcon
              :name="social.icon"
              :size="social.size || '18px'"
              :style="social.style"
            />
          </a>
        </div>

        <div class="tw-flex tw-h-full tw-items-center">
          <ButtonSwitchLocale
            wrapper-class="tw-flex tw-h-full tw-items-center"
            select-class="fast-top-bar-locale tw-text-[16px] tw-leading-6"
            selected-text-class="tw-text-[16px] tw-leading-6"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  SOCIAL_ICONS,
  SOCIAL_LABEL,
  SOCIAL_PLATFORM,
} from '~/constants/social';

const contactStore = useContactStore();
const langStore = useLanguageStore();
const showLocale = computed(() => langStore.value.length > 1);

const contact = computed(() => ({
  phoneNumbers: contactStore.phoneNumbers,
  facebookId: contactStore.facebookId,
  lineId: contactStore.lineId,
  instagramId: contactStore.instagramId,
  youtubeLink: contactStore.youtubeLink,
  tiktokLink: contactStore.tiktokLink,
}));

const allSocialLinks = computed(() => {
  const links: Record<
    string,
    { icon: string; link: string; title: string; size: string; style: string }
  > = {};

  if (contact.value.facebookId) {
    links[SOCIAL_PLATFORM.FACEBOOK] = {
      icon: SOCIAL_ICONS.FACEBOOK,
      link: `https://facebook.com/${contact.value.facebookId}`,
      title: SOCIAL_LABEL.FACEBOOK,
      size: '18px',
      style: '',
    };
  }
  if (contact.value.lineId) {
    links[SOCIAL_PLATFORM.LINE] = {
      icon: SOCIAL_ICONS.LINE,
      link: `https://line.me/R/ti/p/${contact.value.lineId}`,
      title: SOCIAL_LABEL.LINE,
      size: '18px',
      style: '',
    };
  }
  if (contact.value.instagramId) {
    links[SOCIAL_PLATFORM.INSTAGRAM] = {
      icon: SOCIAL_ICONS.INSTAGRAM,
      link: `https://instagram.com/${contact.value.instagramId}`,
      title: SOCIAL_LABEL.INSTAGRAM,
      size: '18px',
      style: '',
    };
  }
  if (contact.value.youtubeLink) {
    links[SOCIAL_PLATFORM.YOUTUBE] = {
      icon: SOCIAL_ICONS.YOUTUBE,
      link: contact.value.youtubeLink,
      title: SOCIAL_LABEL.YOUTUBE,
      size: '18px',
      style: '',
    };
  }
  if (contact.value.tiktokLink) {
    links[SOCIAL_PLATFORM.TIKTOK] = {
      icon: SOCIAL_ICONS.TIKTOK,
      link: contact.value.tiktokLink,
      title: SOCIAL_LABEL.TIKTOK,
      size: '18px',
      style: '',
    };
  }

  return links;
});

const socialLinks = allSocialLinks;
const hasAnySocial = computed(
  () => Object.keys(allSocialLinks.value).length > 0,
);

const { analyticEvent } = useAnalytic();

const eventTopBarTel = (tel: string) => {
  analyticEvent({
    ga4ActionName: 'click_cta_topbar_phone',
    ga4Label: tel,
  });
};

const eventTopBarsocial = (social: string, url: string) => {
  analyticEvent({
    ga4ActionName: `click_cta_topbar_${social}`,
    ga4Label: url,
  });
};
</script>

<style scoped>
.fast-top-bar-phone-icon {
  display: inline-flex;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  line-height: 1;
  vertical-align: middle;
}

.fast-top-bar-action {
  display: inline-flex;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  line-height: 1;
  vertical-align: middle;
}

.fast-top-bar-action :deep(.q-icon) {
  display: inline-flex;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  line-height: 1;
}

.fast-top-bar-phone-icon:deep(.q-icon) {
  display: inline-flex;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  line-height: 1;
}

.fast-top-bar-action :deep(.q-icon svg),
.fast-top-bar-phone-icon:deep(.q-icon svg) {
  width: 18px;
  height: 18px;
}

.fast-top-bar-separator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  margin-right: 4px;
  line-height: 24px;
  vertical-align: middle;
}

.fast-top-bar-locale:deep(.q-field__control),
.fast-top-bar-locale:deep(.q-field__native),
.fast-top-bar-locale:deep(.q-field__append) {
  min-height: 40px;
  height: 40px;
  align-items: center;
}

.fast-top-bar-locale:deep(.q-field__marginal) {
  height: 40px;
  align-items: center;
}
</style>
